<script setup>
import { computed, defineEmits, defineProps, onMounted, ref } from "vue";
import ProductService from "@/services/ProductService";

const props = defineProps({
  entries: { type: Object, required: true },
  editable: { type: Boolean, required: false },
});

const emit = defineEmits(["update:entries"]);

const extras = ref([]);
const error = ref("");

onMounted(async () => {
  const svc = new ProductService();
  const products = await svc.index();
  extras.value = products.filter((p) => p.is_optional_extra);
});

const cloneEntries = (val) => JSON.parse(JSON.stringify(val ?? { dates: [] }));

const selectedExtraIds = computed(
  () =>
    new Set(
      (props.entries?.dates ?? [])
        .flatMap((date) => date?.resources ?? [])
        .filter((resource) => resource?.notes === "Optional extra")
        .map((resource) =>
          Number(resource?.product_id ?? resource?.product?.id),
        ),
    ),
);
const existingProductIds = computed(
  () =>
    new Set(
      (props.entries?.dates ?? [])
        .flatMap((date) => date?.resources ?? [])
        .map((resource) =>
          Number(resource?.product_id ?? resource?.product?.id),
        ),
    ),
);

const addExtra = (product) => {
  if (!props.editable) return;
  const updated = cloneEntries(props.entries);
  const date = updated?.dates?.[0];
  if (!date) {
    error.value = "Add a service date before choosing optional extras.";
    return;
  }
  if (selectedExtraIds.value.has(Number(product.id))) return;
  if (existingProductIds.value.has(Number(product.id))) {
    error.value = `${product.name} is already included in the cover plan.`;
    return;
  }
  if (!Array.isArray(date.resources)) date.resources = [];
  error.value = "";
  date.resources.push({
    product,
    product_id: product.id,
    quantity: 1,
    notes: "Optional extra",
  });
  emit("update:entries", updated);
};

const removeExtra = (product) => {
  if (!props.editable) return;
  const updated = cloneEntries(props.entries);
  (updated?.dates ?? []).forEach((date) => {
    date.resources = (date?.resources ?? []).filter(
      (resource) =>
        !(
          resource?.notes === "Optional extra" &&
          Number(resource?.product_id ?? resource?.product?.id) ===
            Number(product.id)
        ),
    );
  });
  emit("update:entries", updated);
};

const toggleExtra = (product) => {
  if (selectedExtraIds.value.has(Number(product.id))) {
    removeExtra(product);
  } else {
    addExtra(product);
  }
};
</script>

<template>
  <FormBox header="Optional Extras">
    <div class="space-y-2">
      <p v-if="error" class="text-sm text-rose-600" role="alert">{{ error }}</p>
      <div v-if="!extras.length" class="text-sm text-slate-500">
        No optional extras configured.
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          v-for="extra in extras"
          :key="extra.id"
          class="border rounded p-3 flex items-center justify-between"
        >
          <div>
            <div class="font-medium">{{ extra.name }}</div>
            <div class="text-xs text-slate-500">{{ extra.short }}</div>
          </div>
          <button
            class="btn"
            :class="selectedExtraIds.has(Number(extra.id)) ? '' : 'accent'"
            type="button"
            @click.prevent="toggleExtra(extra)"
            :disabled="
              !editable ||
              (existingProductIds.has(Number(extra.id)) &&
                !selectedExtraIds.has(Number(extra.id)))
            "
          >
            {{
              selectedExtraIds.has(Number(extra.id))
                ? "Remove"
                : existingProductIds.has(Number(extra.id))
                  ? "Already added"
                  : "Add"
            }}
          </button>
        </div>
      </div>
    </div>
  </FormBox>
</template>
